import React, { useContext, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

// Styles
import {
  CustomCollapse,
  DropdownBox,
  NavBox,
  NavLink,
  StyledMenuBox,
  StyledMKTypography,
  StyledHeaderStack,
  StyledDividerBox,
  StyledNavTitle,
  StyledCategoryWrapper,
  StyledSubmenuArrow,
  StyledSubmenuDropdown,
  StyledSubmenuItem,
} from "./index.styles";

// components
import { Collapse } from "@mui/material";
import MKTypography from "components/MKTypography";
import CBDivider from "components/CBDivider";
import LoginAndLogOut from "components/CBLoginLogOut";
import ShoppingCart from "components/CBShoppingCart";
import useMediaQueries from "utils/mediaQueries.utils";
import { AuthContext } from "utils/context/authentication.context";
import { CartContext } from "utils/context/cart.context";
import { DirectorySearchBar } from "utils/context/directorySearchBar.context";

const MobileNavList = ({ routes, mobileNavToggle, setMobileNavToggle }) => {
  const { isMd } = useMediaQueries();
  const [open, setOpen] = useState(null);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const { setSearchTerms, setFilterOptions, setPage } = useContext(DirectorySearchBar);
  const { isAuthorized } = useContext(AuthContext);
  const { cartItems } = useContext(CartContext);
  const menuRef = useRef(null);

  const handleItemClick = (index, item) => {
    if (item.collapse) {
      if (open === index) {
        setOpen(null);
        setOpenSubmenu(null); // Close submenu when closing parent
      } else {
        setOpen(index);
        setOpenSubmenu(null); // Reset submenu when opening new parent
      }
    } else {
      setMobileNavToggle(false);
    }
  };

  const handleCategoryClick = (catIndex, category) => {
    if (category.submenu) {
      setOpenSubmenu(openSubmenu === catIndex ? null : catIndex);
    } else if (category.route) {
      setMobileNavToggle(false);
    }
  };

  const closeMenuOnOutsideClick = (event) => {
    if (menuRef.current && !menuRef.current.contains(event.target)) {
      setMobileNavToggle(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", closeMenuOnOutsideClick);
    return () => {
      document.removeEventListener("mousedown", closeMenuOnOutsideClick);
    };
  }, [menuRef]);

  const handleClick = (name) => {
    if (name === "Find A Therapist") {
      setFilterOptions([]);
      setSearchTerms("");
      setPage(1);
    }
  };

  return (
    <CustomCollapse in={Boolean(mobileNavToggle)} timeout={"auto"} unmountOnExit>
      <StyledMenuBox ref={menuRef}>
        <StyledHeaderStack direction={"row"} gap={2} justifyContent={"space-between"}>
          {!isMd && <ShoppingCart />}
          <LoginAndLogOut setMobileNavToggle={setMobileNavToggle} mobileNavToggle={mobileNavToggle} />
        </StyledHeaderStack>
        {!isMd && (cartItems.length > 0 || isAuthorized) && (
          <StyledDividerBox>
            <CBDivider />
          </StyledDividerBox>
        )}
        {routes.map((item, index) => (
          <NavBox key={item.name}>
            {/* TOP LEVEL NAVIGATION ITEM */}
            <NavLink
              variant="button"
              color="inherit"
              component={item.route ? Link : "span"}
              to={item.route || null}
              onClick={() => handleItemClick(index, item)}
            >
              <StyledNavTitle variant="button" fontWeight="regular" textTransform="capitalize">
                {item.name}
              </StyledNavTitle>
            </NavLink>

            {/* FIRST LEVEL COLLAPSE - Categories */}
            {item.collapse && (
              <Collapse in={Boolean(open === index)} timeout={"auto"} unmountOnExit>
                <DropdownBox>
                  {item.collapse.map((category, catIndex) => (
                    <React.Fragment key={category.key}>
                      {/* CATEGORY ITEM */}
                      <StyledCategoryWrapper
                        onClick={() => {
                          if (!category.submenu) {
                            setOpen(null);
                            handleClick(category.name);
                          } else {
                            handleCategoryClick(catIndex, category);
                          }
                        }}
                        variant="button"
                        py={0.625}
                        px={2}
                        textTransform="capitalize"
                      >
                        {category.submenu ? (
                          <MKTypography variant="body2">{category.name}</MKTypography>
                        ) : (
                          <MKTypography onClick={() => setMobileNavToggle(!mobileNavToggle)} variant="body2" component={Link} to={category.route}>
                            {category.name}
                          </MKTypography>
                        )}
                        {category.submenu && (
                          <StyledSubmenuArrow variant="body2" isOpen={openSubmenu === catIndex}>
                            ›
                          </StyledSubmenuArrow>
                        )}
                      </StyledCategoryWrapper>

                      {/* SECOND LEVEL COLLAPSE - Submenu Items */}
                      {category.submenu && (
                        <Collapse in={Boolean(openSubmenu === catIndex)} timeout={"auto"} unmountOnExit>
                          <StyledSubmenuDropdown>
                            {category.submenu.map((subItem) => (
                              <StyledMKTypography
                                key={subItem.key}
                                onClick={() => {
                                  setOpen(null);
                                  setOpenSubmenu(null);
                                  handleClick(subItem.name);
                                }}
                                variant="button"
                                py={0.625}
                                px={2}
                                textTransform="capitalize"
                              >
                                <StyledSubmenuItem
                                  onClick={() => setMobileNavToggle(!mobileNavToggle)}
                                  variant="body2"
                                  component={Link}
                                  to={subItem.route}
                                >
                                  {subItem.name}
                                </StyledSubmenuItem>
                              </StyledMKTypography>
                            ))}
                          </StyledSubmenuDropdown>
                        </Collapse>
                      )}
                    </React.Fragment>
                  ))}
                </DropdownBox>
              </Collapse>
            )}
          </NavBox>
        ))}
      </StyledMenuBox>
    </CustomCollapse>
  );
};

export default MobileNavList;
